import { BriefcaseBusiness, ExternalLink } from "lucide-react";

import { AppLink } from "@/components/app-link";
import { PilotChip, type PilotKey } from "@/components/semantic-badges";
import { StatusBadge, type StatusKey } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toTitleCase } from "@/lib/formatting";
import { cn } from "@/lib/utils";

export interface LinkedRevisionJob {
  pilot: "SpecPilot" | "RevPilot" | "TakeoffPilot";
  targetId: string;
  name: string;
  status: string;
  createdAt: number;
}

interface LinkedRevisionJobsButtonProps {
  jobs?: LinkedRevisionJob[];
  compact?: boolean;
  hideWhenEmpty?: boolean;
  className?: string;
}

function getLinkedJobRoute(job: LinkedRevisionJob): {
  to: string;
  params: Record<string, string>;
  title: string;
} {
  if (job.pilot === "SpecPilot") {
    return {
      to: "/specPilot/$jobId",
      params: { jobId: job.targetId },
      title: job.name,
    };
  }
  if (job.pilot === "TakeoffPilot") {
    return {
      to: "/takeoffPilot/$jobId",
      params: { jobId: job.targetId },
      title: job.name,
    };
  }
  return {
    to: "/revPilot/$comparisonId",
    params: { comparisonId: job.targetId },
    title: job.name,
  };
}

const PILOT_KEYS: Record<LinkedRevisionJob["pilot"], PilotKey> = {
  SpecPilot: "spec",
  TakeoffPilot: "takeoff",
  RevPilot: "rev",
};

/** Linked jobs span several pilots' status enums; map the shared vocabulary onto status tokens. */
const LINKED_JOB_STATUS_KEYS: Record<string, StatusKey> = {
  pending: "pending",
  queued: "pending",
  extracting: "pending",
  processing: "processing",
  running: "processing",
  in_progress: "processing",
  ready_for_review: "review",
  in_review: "review",
  review: "review",
  completed: "completed",
  complete: "completed",
  failed: "error",
  error: "error",
};

function LinkedJobStatusBadge({ status }: { status: string }) {
  const statusKey = LINKED_JOB_STATUS_KEYS[status];
  return (
    <StatusBadge
      status={statusKey ?? "pending"}
      tone={statusKey ? "default" : "muted"}
      label={toTitleCase(status)}
      showSpinner={false}
    />
  );
}

export function LinkedRevisionJobsButton({
  jobs = [],
  compact = false,
  hideWhenEmpty = false,
  className,
}: LinkedRevisionJobsButtonProps) {
  const uniqueJobs = [...new Map(jobs.map((job) => [`${job.pilot}:${job.targetId}`, job])).values()];

  if (hideWhenEmpty && uniqueJobs.length === 0) {
    return null;
  }

  const label = compact
    ? String(uniqueJobs.length)
    : uniqueJobs.length === 1
      ? "1 linked job"
      : `${uniqueJobs.length} linked jobs`;
  const ariaLabel = uniqueJobs.length === 0
    ? "No linked jobs"
    : compact
      ? `${uniqueJobs.length} linked job${uniqueJobs.length === 1 ? "" : "s"} for this revision`
      : `${label} for this revision`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={compact ? "icon-xs" : "sm"}
          disabled={uniqueJobs.length === 0}
          className={cn(compact && "rounded-full", className)}
          aria-label={ariaLabel}
          onClick={(event) => event.stopPropagation()}
        >
          <BriefcaseBusiness className="size-3.5" aria-hidden="true" />
          {compact ? <span className="sr-only">{label}</span> : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 space-y-3 border-2 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <div className="text-sm font-semibold">Linked jobs</div>
          <div className="text-xs text-muted-foreground">
            SpecPilot, TakeoffPilot, and RevPilot jobs connected to this revision.
          </div>
        </div>
        <div className="space-y-2">
          {uniqueJobs.map((job) => {
            const route = getLinkedJobRoute(job);
            return (
              <AppLink
                key={`${job.pilot}:${job.targetId}`}
                to={route.to}
                params={route.params}
                title={route.title}
                className="block rounded-lg border-2 bg-background p-3 text-left transition hover:bg-muted/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="truncate text-sm font-semibold">{job.name}</div>
                  </div>
                  <ExternalLink className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <PilotChip pilot={PILOT_KEYS[job.pilot]} />
                  <LinkedJobStatusBadge status={job.status} />
                </div>
              </AppLink>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
